import type { AttributionData } from "@/lib/analytics/utm-tracker";

export const REVENUECAT_ATTRIBUTE_VALUE_MAX_LENGTH = 500;

export type RevenueCatAttributeValues = Record<string, string>;
export type RevenueCatV1Attributes = Record<string, { value: string }>;

type BuildRevenueCatAttributionAttributesOptions = {
  includeReservedAttributionKeys?: boolean;
};

export function truncateRevenueCatAttributeValue(value?: string | null): string | undefined {
  if (!value) return undefined;
  return value.length > REVENUECAT_ATTRIBUTE_VALUE_MAX_LENGTH
    ? value.slice(0, REVENUECAT_ATTRIBUTE_VALUE_MAX_LENGTH)
    : value;
}

export function buildRevenueCatAttributionAttributes(
  attribution: AttributionData,
  options: BuildRevenueCatAttributionAttributesOptions = {}
): RevenueCatAttributeValues {
  const includeReservedAttributionKeys = options.includeReservedAttributionKeys ?? true;
  const attributes: RevenueCatAttributeValues = {};
  const setAttribute = (key: string, value?: string) => {
    const truncatedValue = truncateRevenueCatAttributeValue(value);
    if (truncatedValue) attributes[key] = truncatedValue;
  };

  // RevenueCat reserves the "$" prefix. Only use documented reserved
  // attribution keys there; keep repo-owned first-touch fields custom.
  if (includeReservedAttributionKeys) {
    setAttribute("$mediaSource", attribution.network || attribution.utm_source);
    setAttribute("$campaign", attribution.utm_campaign);
    setAttribute("$adGroup", attribution.ad_group);
    setAttribute("$keyword", attribution.keywords || attribution.utm_term);
    setAttribute("$creative", attribution.creative || attribution.utm_content);
  }

  setAttribute("utm_source", attribution.utm_source);
  setAttribute("utm_medium", attribution.utm_medium);
  setAttribute("utm_campaign", attribution.utm_campaign);
  setAttribute("utm_term", attribution.utm_term);
  setAttribute("utm_content", attribution.utm_content);
  setAttribute("initial_referrer", attribution.referrer);
  setAttribute("initial_landing_page", attribution.landing_page);
  setAttribute("first_visit", attribution.first_visit);
  setAttribute("gclid", attribution.gclid);
  setAttribute("gbraid", attribution.gbraid);
  setAttribute("wbraid", attribution.wbraid);
  setAttribute("dclid", attribution.dclid);
  setAttribute("gad_source", attribution.gad_source);
  setAttribute("gad_campaignid", attribution.gad_campaignid);
  setAttribute("fbclid", attribution.fbclid);
  setAttribute("ad_network", attribution.network);
  setAttribute("ad_group", attribution.ad_group);
  setAttribute("keywords", attribution.keywords);
  setAttribute("creative", attribution.creative);
  setAttribute("latest_paid_source", attribution.latest_paid_source);
  setAttribute("latest_paid_medium", attribution.latest_paid_medium);
  setAttribute("latest_paid_campaign", attribution.latest_paid_campaign);
  setAttribute("latest_paid_term", attribution.latest_paid_term);
  setAttribute("latest_paid_content", attribution.latest_paid_content);
  setAttribute("latest_paid_gclid", attribution.latest_paid_gclid);
  setAttribute("latest_paid_gbraid", attribution.latest_paid_gbraid);
  setAttribute("latest_paid_wbraid", attribution.latest_paid_wbraid);
  setAttribute("latest_paid_dclid", attribution.latest_paid_dclid);
  setAttribute("latest_paid_gad_source", attribution.latest_paid_gad_source);
  setAttribute("latest_paid_gad_campaignid", attribution.latest_paid_gad_campaignid);
  setAttribute("latest_paid_network", attribution.latest_paid_network);
  setAttribute("latest_paid_ad_group", attribution.latest_paid_ad_group);
  setAttribute("latest_paid_keywords", attribution.latest_paid_keywords);
  setAttribute("latest_paid_creative", attribution.latest_paid_creative);
  setAttribute("latest_paid_landing_page", attribution.latest_paid_landing_page);
  setAttribute("latest_paid_referrer", attribution.latest_paid_referrer);
  setAttribute("latest_paid_visit", attribution.latest_paid_visit);

  return attributes;
}

export function buildRevenueCatV1AttributionAttributes(
  attribution: AttributionData,
  options: BuildRevenueCatAttributionAttributesOptions = {}
): RevenueCatV1Attributes {
  return Object.fromEntries(
    Object.entries(buildRevenueCatAttributionAttributes(attribution, options)).map(
      ([key, value]) => [key, { value }]
    )
  );
}
